import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../i18n/LanguageContext";
import { SEO_DATA, SERVICE_KEYS, pathFor } from "../../i18n/seoMeta";

// Internal-linking block. Beyond the existing CTA, this section now lists
// every service detail page as a deep link from the homepage — Google needs
// these to discover & crawl the subpages, which fixes the
// "Discovered – currently not indexed" status in Search Console.
export const MostRequested = () => {
  const { lang, t } = useLanguage();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="most-requested"
      data-testid="most-requested-section"
      className="bg-[#F3F4F5] text-[#181B24] py-16 md:py-20"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-6">
            <p
              data-testid="most-requested-eyebrow"
              className="font-body italic text-[#181B24]/70 text-base md:text-lg mb-5"
            >
              {t.mostRequested.eyebrow}
            </p>
            <h2
              data-testid="most-requested-headline"
              className="font-heading uppercase font-bold leading-[1.02] tracking-[0.01em] text-4xl sm:text-5xl md:text-6xl lg:text-7xl"
            >
              {t.mostRequested.headlineLines.map((line, i) => (
                <span key={i}>
                  {line}
                  <br />
                </span>
              ))}
              <span className="gradient-text">{t.mostRequested.headlineAccent}</span>
            </h2>
          </div>

          <div className="lg:col-span-6 lg:pl-12 relative">
            <div
              aria-hidden="true"
              className="hidden lg:block absolute left-0 top-2 bottom-2 w-[3px] bg-[#FFE21A]"
            />
            <p
              data-testid="most-requested-paragraph"
              className="text-[#181B24]/80 text-base md:text-lg leading-relaxed font-body"
            >
              {t.mostRequested.paragraph}
            </p>
            <button
              data-testid="most-requested-cta"
              onClick={() => scrollTo("contact")}
              className="mt-8 group inline-flex items-center gap-2 rounded-full bg-[#181B24] text-white font-heading font-bold uppercase tracking-[0.15em] text-sm px-7 py-4 hover:bg-[#202535] transition-colors"
            >
              {t.mostRequested.cta}
              <ArrowRight
                size={16}
                className="group-hover:translate-x-1 transition-transform"
              />
            </button>
          </div>
        </div>

        {/* Service deep-links — internal linking for SEO crawl discovery */}
        <div className="mt-12 md:mt-16 pt-8 md:pt-10 border-t border-[#181B24]/10">
          <p
            data-testid="featured-services-label"
            className="font-heading uppercase tracking-[0.18em] text-xs md:text-sm text-[#181B24]/55 mb-5"
          >
            {lang === "cs" ? "Detail služeb" : "Service detail pages"}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {SERVICE_KEYS.map((id) => {
              const sm = SEO_DATA[lang][id];
              return (
                <Link
                  key={id}
                  to={pathFor(id, lang)}
                  data-testid={`featured-service-link-${id}`}
                  className="group inline-flex items-center justify-between gap-3 rounded-xl bg-white px-5 py-4 ring-1 ring-[#181B24]/10 hover:ring-[#FFE21A] hover:bg-[#FFFEF5] transition-all"
                >
                  <span className="font-heading font-bold uppercase tracking-[0.02em] text-sm md:text-base text-[#181B24] leading-tight">
                    {sm.h1}
                  </span>
                  <ArrowUpRight
                    size={18}
                    className="shrink-0 text-[#181B24]/60 group-hover:text-[#181B24] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                  />
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MostRequested;
